import { broadcastUpdatedDocumentation } from 'serviceWorker/broadcasts/documentations';
import * as documentations from 'serviceWorker/db/documentations/documentations';
import { get as getCurrentProject } from 'serviceWorker/db/selectedProject';
import { documentationFilteringCacheManager } from 'serviceWorker/repository/documentation/documentationFilteringCacheManager/index';
import {
  isExportAsVersionDto,
  type ExportDocumentOutDto,
  type ExportToNewDocumentationDto,
} from 'shared/domain/issue/mapping/exportDocument/toOut/createExportDocumentOutDto';
import type { ExportToDocumentationResultData } from 'shared/domain/issue/linkedDocumentation/operations';
import { ImageConvertStatus } from 'shared/domain/imageConvertStatus/imageConvertStatus';
import { DocumentationInDto } from 'shared/dtos/in/documentation';
import { ImageRotation } from 'shared/dtos/out/document';
import { UploadStatus } from 'shared/types/uploadStatus';
import { nowISO } from 'shared/utils/date/dates';
import { resolveExtensionFromFileName } from 'shared/utils/fileExtensions';
import { EXTENSIONS } from 'shared/constants/EXTENSIONS';
import { ROOT_DIR_ID } from 'shared/constants/directory';
import { scopeOfProject } from 'serviceWorker/helpers/dataScope';

type UpsertExportedDocumentationOptions = {
  imageRotation?: number;
  preferredExtension?: string;
};

function buildExportedDocumentationInDto(
  data: ExportToDocumentationResultData,
  payload: ExportToNewDocumentationDto,
  options?: UpsertExportedDocumentationOptions
): DocumentationInDto {
  const now = nowISO();
  const fileName = data.documentationName ?? payload.name;
  const extension =
    options?.preferredExtension?.replace(/^\./, '') ||
    resolveExtensionFromFileName(fileName) ||
    EXTENSIONS.PDF;

  return {
    _id: data.documentationId,
    name: fileName,
    number: payload.number ?? '',
    parentId: payload.isParentRootDirectory ? undefined : payload.parentId,
    deleted: false,
    createdAt: now,
    modifiedAt: now,
    // The backend creates the documentation without a current version; the export sets it afterwards.
    currentVersionId: null,
    versions: [
      {
        _id: data.versionId,
        deleted: false,
        createdAt: now,
        modifiedAt: now,
        uploadStatus: UploadStatus.success,
        extension,
        remoteVersionId: '',
        imageConvertStatus: ImageConvertStatus.SUCCESS,
        name: fileName,
        number: 1,
        imageRotation:
          typeof options?.imageRotation === 'number'
            ? (options.imageRotation as ImageRotation)
            : ImageRotation.ROTATE_0,
      },
    ],
  };
}

async function persistDocumentationDto(
  documentation: DocumentationInDto,
  projectId: string
): Promise<void> {
  await scopeOfProject(projectId).write(async () => {
    await documentations.updateBatch([documentation]);
    documentationFilteringCacheManager.invalidateDocumentationFilteringCachesByParentIds(
      [documentation.parentId ?? ROOT_DIR_ID]
    );
    broadcastUpdatedDocumentation({
      _id: documentation._id,
      parentId: documentation.parentId,
    });
  });
}

/**
 * Stores the exported documentation at once, so the folder shows it before the sync that follows
 * the export brings what the server saved. The API has no read of one documentation by id
 * (`GET /documentation/:id` answers 404), so the record is built from the export's answer.
 */
export async function upsertExportedDocumentationAfterExport(
  data: ExportToDocumentationResultData,
  payload: ExportDocumentOutDto,
  options?: UpsertExportedDocumentationOptions
): Promise<void> {
  const project = await getCurrentProject();
  if (!project?._id || !data.documentationId) {
    return;
  }

  // The documentation exists on this device already, with versions this record would drop: the
  // sync that follows the export brings the new one.
  if (isExportAsVersionDto(payload)) {
    return;
  }

  const exportedDocumentation = buildExportedDocumentationInDto(
    data,
    payload,
    options
  );
  await persistDocumentationDto(exportedDocumentation, project._id);
}
